import { NavLink as MantineNavLink, Box, ScrollArea, Text } from '@mantine/core';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Cpu, CreditCard, type LucideIcon } from 'lucide-react';

interface SettingsNavItem {
    path: string;
    label: string;
    icon: LucideIcon;
}

const ITEMS: SettingsNavItem[] = [
    { path: '/operator/settings/models', label: 'Models', icon: Cpu },
    { path: '/operator/settings/billing', label: 'Billing integration', icon: CreditCard },
];

/**
 * Operator Settings shell — a full-height 240px section rail flush against the
 * navbar beside a scrollable content area, matching the tenant app's Settings
 * shell (`FeatureNavLayout`): body-tone rail, title header, 13px nav labels.
 */
export default function OperatorSettingsLayout(): React.ReactElement {
    const location = useLocation();
    const navigate = useNavigate();
    const frameHeight = 'calc(100vh - 56px)';

    return (
        <Box style={{ display: 'flex', height: frameHeight }}>
            <Box
                style={{
                    width: 240,
                    flexShrink: 0,
                    height: '100%',
                    background: 'var(--bm-color-body)',
                    borderRight: '1px solid var(--bm-color-border)',
                }}
            >
                <Box px={22} pt={38} pb={34}>
                    <Text size="sm" fw={700} style={{ color: 'var(--bm-color-text)' }}>Settings</Text>
                </Box>
                <Box px="sm">
                    {ITEMS.map((item) => {
                        const Icon = item.icon;
                        const active = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                        return (
                            <MantineNavLink
                                key={item.path}
                                label={item.label}
                                leftSection={<Icon size={16} strokeWidth={active ? 2.5 : 2} />}
                                active={active}
                                onClick={() => navigate(item.path)}
                                variant="light"
                                mb={2}
                                styles={{ label: { fontWeight: active ? 700 : 500, fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}
                            />
                        );
                    })}
                </Box>
            </Box>

            <Box style={{ flex: 1, minWidth: 0 }}>
                <ScrollArea h={frameHeight} type="hover" scrollbarSize={8} styles={{ thumb: { backgroundColor: 'var(--mantine-color-zinc-3)', borderRadius: 4 } }}>
                    <Box p="md" px={{ base: 'md', sm: 48 }}>
                        <Box maw={900}>
                            <Outlet />
                        </Box>
                    </Box>
                </ScrollArea>
            </Box>
        </Box>
    );
}
